import { DatePipe } from '@angular/common';
import { LOCALE_ID, Pipe, PipeTransform, inject } from '@angular/core';

/**
 * The only date/time formats used in the app. All numeric and 24-hour, so the output is identical
 * regardless of the active language (no month names, no AM/PM, no locale-dependent separators).
 */
export const APP_DATE_FORMATS = {
    date: 'd. M. yyyy',
    datetime: 'd. M. yyyy HH:mm',
    time: 'HH:mm',
} as const;

export type AppDateFormat = keyof typeof APP_DATE_FORMATS;

/**
 * Formats `value` in the viewer's local timezone (API timestamps carry a "Z", i.e. UTC).
 * Like Angular's DatePipe, returns null for empty input so `?? fallback` in templates keeps working;
 * unparseable input also yields null instead of throwing.
 */
export function formatAppDate(
    value: Date | string | number | null | undefined,
    format: AppDateFormat = 'datetime',
    datePipe: DatePipe = new DatePipe('en-US'),
): string | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    try {
        return datePipe.transform(value, APP_DATE_FORMATS[format]);
    } catch {
        return null;
    }
}

/**
 * Converts the value of a `<input type="datetime-local">` (naked local time, e.g. "2026-09-19T14:00")
 * to a UTC ISO instant ("2026-09-19T12:00:00.000Z") for the API, which treats offset-less values as UTC.
 * Empty or unparseable input is returned unchanged.
 */
export function localDateTimeToUtcIso(value: string): string {
    if (!value) {
        return value;
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? value : parsed.toISOString();
}

@Pipe({ name: 'appDate' })
export class AppDatePipe implements PipeTransform {
    private readonly datePipe = new DatePipe(inject(LOCALE_ID));

    transform(value: Date | string | number | null | undefined, format: AppDateFormat = 'datetime'): string | null {
        return formatAppDate(value, format, this.datePipe);
    }
}
